import { useState, useEffect, type ReactNode } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { captureError } from "@/lib/errorReporting";
import { TopBar, Button, SkeletonCard, Icon } from "@/components/ui";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

/* ─── Types ─── */
interface TopVoice {
    handle: string;
    name: string;
    imageUrl?: string;
    tweetLink?: string;
    impressionCount?: number;
}

interface CoinDetail {
    _id: string;
    name: string;
    ticker: string;
    slug: string;
    imageUrl?: string;
    lore?: string;
    mcapFormatted: string;
    volumeFormatted: string;
    viewsFormatted: string;
    paRatio?: number;
    change24h: number;
    change24hFormatted: string;
    holders: string;
    tradeUrl?: string;
    topVoices: TopVoice[];
    tokenAlreadyCreated?: boolean;
}

/* ════════════════════════════════════════════
   Coin Page — Token Analytics
   ════════════════════════════════════════════ */
export default function CoinPage(): ReactNode {
    const { coinId } = useParams<{ coinId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const passedCoin = (location.state as { coin?: CoinDetail })?.coin ?? null;
    const [coin, setCoin] = useState<CoinDetail | null>(passedCoin);
    const [loading, setLoading] = useState(!passedCoin);

    useEffect(() => {
        if (!coinId || passedCoin) return;
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/coins/${coinId}`, { signal: controller.signal })
            .then((r) => {
                if (!r.ok) throw new Error("Not found");
                return r.json();
            })
            .then((data) => setCoin(data))
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, [coinId, passedCoin]);

    return (
        <div className="relative flex min-h-screen w-full flex-col overflow-hidden max-w-[430px] mx-auto border-x border-white/5 shadow-2xl bg-background-dark">
            <div className="fixed inset-0 grid-pattern opacity-40 pointer-events-none" />
            <div className="fixed top-1/4 left-1/2 -translate-x-1/2 w-[250px] h-[250px] xs:w-[300px] xs:h-[300px] bg-primary/20 blur-[100px] rounded-full pointer-events-none" />

            <TopBar showBack onBack={() => navigate(-1)} />

            <main className="relative z-10 flex-1 flex flex-col px-4 pt-4 pb-36">
                {loading ? (
                    <div className="space-y-4">
                        <div className="w-full h-28 rounded-3xl bg-white/5 animate-pulse" />
                        <div className="grid grid-cols-2 gap-3">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-20 rounded-lg xs:rounded-2xl bg-white/5 animate-pulse" />
                            ))}
                        </div>
                        <SkeletonCard count={2} />
                    </div>
                ) : !coin ? (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-white/40 font-inter">Coin not found</p>
                    </div>
                ) : (
                    <>
                        {/* ─── Hero Card ─── */}
                        <HeroCard coin={coin} />

                        {/* ─── Stat Grid ─── */}
                        <StatGrid coin={coin} />

                        {/* ─── Top Voices ─── */}
                        {coin.topVoices.length > 0 && (
                            <VoicesSection voices={coin.topVoices} />
                        )}
                    </>
                )}
            </main>

            {/* ─── Bottom CTA ─── */}
            {coin && (
                <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] bg-background-dark/95 backdrop-blur-xl border-t border-white/10 pb-6 px-4 xs:px-6 py-4 z-50 flex gap-3">
                    {/* {coin.tradeUrl && (
                        <Button
                            variant="primary"
                            size="lg"
                            fullWidth
                            icon="open-in-new"
                            onClick={() => window.open(coin.tradeUrl, "_blank")}
                        >
                            Trade
                        </Button>
                    )} */}
                    <Button
                        variant="cta-pink"
                        size="lg"
                        fullWidth
                        icon="bolt"
                        disabled={coin.tokenAlreadyCreated}
                        onClick={() => navigate(`/create?coin=${coin.slug}`)}
                    >
                        {coin.tokenAlreadyCreated ? "Already Launched" : "CODEV It"}
                    </Button>
                </div>
            )}
        </div>
    );
}

/* ─── Hero Card ─── */
function HeroCard({ coin }: { coin: CoinDetail }): ReactNode {
    return (
        <div className="bg-card-dark/80 p-4 xs:p-6 rounded-2xl xs:rounded-3xl neon-border-lime backdrop-blur-md mb-4 xs:mb-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-lime/10 blur-3xl -mr-16 -mt-16" />
            <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-lime to-primary flex items-center justify-center border border-accent-lime/30 overflow-hidden shrink-0">
                    {coin.imageUrl ? (
                        <img
                            alt={coin.ticker}
                            className="w-full h-full object-cover"
                            src={coin.imageUrl}
                        />
                    ) : (
                        <span className="text-2xl font-black italic text-background-dark">
                            {coin.ticker.replace("$", "").charAt(0)}
                        </span>
                    )}
                </div>
                <div className="flex-1">
                    <h1 className="text-2xl xs:text-3xl font-black italic uppercase tracking-tighter truncate">
                        {coin.ticker}
                    </h1>
                    <p className="text-white/40 text-xs uppercase font-bold tracking-[0.2em] mt-1 truncate">
                        {coin.name}
                    </p>
                </div>
            </div>
            {coin.lore && (
                <p className="text-white/50 text-[11px] font-inter leading-relaxed mt-4">
                    {coin.lore}
                </p>
            )}
        </div>
    );
}

/* ─── Stat Grid (2x2) ─── */
function StatGrid({ coin }: { coin: CoinDetail }): ReactNode {
    const stats = [
        { label: "Mcap", value: coin.mcapFormatted, icon: "payments", color: "text-accent-lime" },
        { label: "Volume 24H", value: coin.volumeFormatted, icon: "bar-chart", color: "text-accent-pink" },
        { label: "Holders", value: coin.holders, icon: "group", color: "text-primary" },
        {
            label: "24H Change",
            value: coin.change24hFormatted,
            icon: coin.change24h >= 0 ? "trending-up" : "trending-down",
            color: coin.change24h >= 0 ? "text-accent-lime" : "text-red-400",
        },
    ];

    return (
        <div className="grid grid-cols-2 gap-2 xs:gap-3 mb-4 xs:mb-6">
            {stats.map((s) => (
                <div
                    key={s.label}
                    className="bg-white/5 border border-white/10 rounded-xl xs:rounded-2xl p-2 xs:p-3 flex items-center gap-2 xs:gap-3"
                >
                    <div className={`w-8 h-8 xs:w-10 xs:h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10 ${s.color}`}>
                        <Icon name={s.icon} size={20} />
                    </div>
                    <div>
                        <p className="text-[9px] font-bold text-white/40 uppercase tracking-widest">
                            {s.label}
                        </p>
                        <p className={`text-sm font-black italic ${s.label === "24H Change" ? s.color : ""}`}>
                            {s.value}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ─── Top Voices ─── */
function VoicesSection({ voices }: { voices: TopVoice[] }): ReactNode {
    return (
        <section className="mb-6">
            <h3 className="text-xs font-black uppercase italic tracking-[0.2em] mb-4 flex items-center gap-2">
                Top Voices <span className="w-1 h-1 rounded-full bg-accent-lime" />
            </h3>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-4">
                {voices.map((v, i) => (
                    <a
                        key={v.handle}
                        href={v.tweetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center gap-2 shrink-0"
                    >
                        <div className={`w-14 h-14 rounded-full p-0.5 ${i === 0 ? "bg-gradient-to-tr from-accent-lime to-primary" : "bg-white/10"}`}>
                            {v.imageUrl ? (
                                <img
                                    alt={v.handle}
                                    className="w-full h-full rounded-full object-cover border-2 border-background-dark"
                                    src={v.imageUrl}
                                />
                            ) : (
                                <div className="w-full h-full rounded-full bg-card-dark border-2 border-background-dark flex items-center justify-center">
                                    <Icon name="person" size={24} className="opacity-40" />
                                </div>
                            )}
                        </div>
                        <span className={`text-[9px] font-bold ${i === 0 ? "text-white" : "text-white/60"}`}>
                            {v.handle}
                        </span>
                    </a>
                ))}
            </div>
        </section>
    );
}
